import { AnalyticEvent } from '@/analytics/fennel/registry';
import { CompleteTranslation, ObiItemType } from '@/types';
import { createStyles } from '@/contexts/theme-context';
import { fennelTrack } from '@/analytics/fennel/hook';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { formatDate } from '@/utils/date';
import { getTranslationValue } from '@/utils/translations';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { Text, View, ViewStyle } from 'react-native';
import { useEffect } from 'react';
import { useItemDisplayOptions } from '@/contexts/new-order-context';

interface Props {
  containerStyle?: ViewStyle;
  eventTime: string;
  imageUrls: string[];
  obiType: ObiItemType;
  subtitle: CompleteTranslation;
  title: string;
  variantPackUUID: string;
}

export const ObiCarouselItem: React.FC<Props> = ({
  title,
  imageUrls,
  subtitle,
  eventTime,
  obiType,
  variantPackUUID,
  containerStyle,
}) => {
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const [styles] = useStyles();

  useEffect(() => {
    fennelTrack(AnalyticEvent.OBI_ITEM_VIEW, {
      message: getTranslationValue(subtitle),
      obiItemType: obiType,
      variantPackUUID,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={[styles.container, containerStyle ? containerStyle : null]}>
      <View style={styles.imageContainer}>
        <OrderableItemListItemImage
          alt={title}
          hidePlaceholderImages={hidePlaceholderImages}
          imageURLs={imageUrls}
          listDisplaySize={ListDisplaySize.Medium}
          style={styles.imagePreview}
        />
      </View>
      <View style={styles.flexContainer}>
        <View style={styles.headerContainer}>
          <Text
            numberOfLines={1}
            style={[FONTS.primaryBold, TYPOGRAPHY.h4, styles.title]}
          >
            {title}
          </Text>
          <Text style={[FONTS.primary, TYPOGRAPHY.small, styles.date]}>
            {formatDate(new Date(eventTime))}
          </Text>
        </View>
        <View style={styles.subheaderContainer}>
          <Text
            numberOfLines={1}
            style={[FONTS.primary, TYPOGRAPHY.description, styles.subtitle]}
          >
            {getTranslationValue(subtitle)}
          </Text>
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  date: {
    color: theme.sk.color.text.tertiary,
    lineHeight: 21,
    textAlign: 'right',
  },
  flexContainer: {
    flex: 1,
  },
  headerContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  imageContainer: {
    marginRight: 12,
  },
  imagePreview: {
    borderColor: theme.sk.color.border.image,
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: 1,
  },
  subheaderContainer: {
    flex: 1,
    marginTop: 4,
  },
  subtitle: {
    color: theme.sk.color.text.tertiary,
  },
  title: {
    color: theme.sk.color.text.primary,
    flex: 1,
    fontSize: 16,
    lineHeight: 21,
    marginRight: 24,
  },
}));
